import { StorageKeys } from '@reward-platform/utils/client'
import { useCallback, useEffect, useRef } from 'react'
import * as uuid from 'uuid'
import { z } from 'zod'
import { useRouter } from 'next/router'
import { useSessionStorage } from 'usehooks-ts'

type CorrelationIdHook = () => {
  correlationId: string | undefined
  resetCorrelationId: () => void
  removeCorrelationId: () => void
}

const useCorrelationId: CorrelationIdHook = () => {
  const router = useRouter()
  const hasInitialized = useRef(false)

  const [correlationId, setCorrelationId, removeCorrelationId] = useSessionStorage<
    string | undefined
  >(StorageKeys.CORRELATION_ID, undefined, {
    deserializer: (value) => {
      try {
        const parsed = JSON.parse(value)
        const res = z.string().guid().safeParse(parsed)
        if (res.success) {
          return res.data
        }
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error(`Failed to parse ${parsed} as UUID`)
        }
        return undefined
      } catch {
        return undefined
      }
    },
  })

  const resetCorrelationId = useCallback(() => {
    const newId = uuid.v4()
    setCorrelationId(newId)
  }, [setCorrelationId])

  useEffect(() => {
    if (hasInitialized.current || !router.isReady) {
      return
    }

    hasInitialized.current = true
    const urlCorrelationId = router.query.correlationId as string | undefined

    if (urlCorrelationId) {
      setCorrelationId(urlCorrelationId)
      return
    }
    if (correlationId == null) {
      resetCorrelationId()
    }
  }, [
    router.isReady,
    router.query.correlationId,
    correlationId,
    setCorrelationId,
    resetCorrelationId,
  ])

  return {
    correlationId,
    resetCorrelationId,
    removeCorrelationId,
  }
}
export default useCorrelationId
